import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Progress,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  FetchUserAttributesOutput,
  fetchUserAttributes,
} from "aws-amplify/auth";
import { post } from "aws-amplify/api";
import { COLORS } from "@/utils/GlobalStyles";
import { useUserProvider } from "@/utils/UserProvider";
import { ESSENTIAL_LIMITS, FREE_LIMITS } from "@/utils/checkSubscriptionUsage";
import { addCharacterS } from "@/utils/utils";

const Billing = () => {
  const navigate = useNavigate();
  const { amplifyUser, revcatUser } = useUserProvider();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeStripeSubscription, setActiveStripeSubscription] =
    useState<any>(null);

  const isPremium = revcatUser.premium;
  const isEssential = revcatUser.essential;

  useEffect(() => {
    if (amplifyUser?.email) {
      initStripe();
      fetchData();
    }
  }, [amplifyUser?.email]);

  const initStripe = async (silent: boolean = false) => {
    if (!silent) {
      setLoading(true);
    }
    const [subscriptions] = await Promise.all([onGetListSubscriptions()]);

    const entitlements = (revcatUser as any).customerInfoApi?.entitlements;

    if (!entitlements) {
      return setLoading(false);
    }

    const activeRevenuecatSubscription =
      entitlements.active["Premium"] || entitlements.active["Essential"];

    const activeStripeSubscription = subscriptions.find(
      (sub: any) =>
        activeRevenuecatSubscription?.productIdentifier === sub.plan.product,
    );
    if (activeStripeSubscription) {
      setActiveStripeSubscription(activeStripeSubscription);
    }

    await fetchData();

    if (!silent) {
      setLoading(false);
    }
  };

  const usageResetDate = () => {
    let resetDate = new Date();
    resetDate = new Date(resetDate.getFullYear(), resetDate.getMonth() + 1, 1);
    return moment(resetDate).format("MMM DD, YYYY");
  };

  async function fetchData() {
    try {
      const userInfo: FetchUserAttributesOutput = await fetchUserAttributes();
      setName(userInfo.name!);
    } catch (err) {
      console.log(err);
    }
  }

  const onGetListSubscriptions = async () => {
    if (amplifyUser?.email) {
      try {
        const postGetSubscriptions = post({
          apiName: "StripeBackend",
          path: `/stripe/list-subscriptions`,
          options: {
            body: {
              email: amplifyUser.email,
            },
          },
        });

        const resp = await postGetSubscriptions.response;
        const result: any = await resp.body.json();
        if (
          result &&
          result?.subscriptions &&
          result?.subscriptions?.data.length > 0
        ) {
          return result?.subscriptions?.data;
        }
      } catch (e) {
        console.log(e);
      }
    }
    return [];
  };

  const renderExtraTexts = () => {
    if (!activeStripeSubscription) {
      return null;
    }

    if (activeStripeSubscription.status === "trialing") {
      return (
        <Text size="md" c={COLORS.primary} fw={600}>
          Trailing until:{" "}
          {moment(activeStripeSubscription.current_period_end * 1000).format(
            "MM/DD/YYYY, hh:mm A",
          )}
        </Text>
      );
    }
    if (activeStripeSubscription.status === "active") {
      return (
        <Text size="md" c={COLORS.primary} fw={600}>
          Next invoice:{" "}
          {moment(activeStripeSubscription.current_period_end * 1000).format(
            "MM/DD/YYYY, hh:mm A",
          )}
        </Text>
      );
    }
    if (activeStripeSubscription.status === "canceled") {
      return (
        <Stack gap={0}>
          <Text size="md" c={"red"} fw={600}>
            Canceled at:{" "}
            {moment(activeStripeSubscription.canceled_at * 1000).format(
              "MM/DD/YYYY, hh:mm A",
            )}
          </Text>
          <Text size="md" c={COLORS.primary} fw={600}>
            Expires at:{" "}
            {moment(activeStripeSubscription.current_period_end * 1000).format(
              "MM/DD/YYYY, hh:mm A",
            )}
          </Text>
        </Stack>
      );
    }
    return null;
  };
  return (
    <Box p={15}>
      <Title size={"h3"}>Billing and usage</Title>
      <Title size={"h5"} my={"md"}>
        Your plan
      </Title>
      {loading && <Skeleton height={300} />}
      {!loading && (
        <Card
          shadow="md"
          withBorder
          w={"50%"}
          my={"md"}
          className="box-billing"
        >
          <Flex align={"center"} mb={"sm"} justify={"space-between"}>
            <Flex align={"center"}>
              <Text fw={"500"}>{name || amplifyUser?.email}</Text>
              <Text px={10} fz={14} c={"#403F3E"}>
                {isEssential ? "Essential" : isPremium ? "Premium" : "Free"}
              </Text>
            </Flex>
            <Flex align={"center"}>
              <Text fw={"500"} fz={14} pr={2}>
                Usage reset:
              </Text>
              <Text fz={14}>{usageResetDate()}</Text>
            </Flex>
          </Flex>
          <Box ta={"center"}>
            {amplifyUser !== null && (
              <>
                <Flex align={"center"} justify={"space-between"}>
                  <Text fz={"sm"}>
                    Template{addCharacterS(amplifyUser.currentTemplatesUsed)}{" "}
                    Used
                  </Text>
                  <Text mx={"sm"}>
                    {amplifyUser === null
                      ? 0
                      : amplifyUser.currentTemplatesUsed}
                    {` / `}
                    {isEssential
                      ? ESSENTIAL_LIMITS.templates
                      : isPremium
                        ? "Unlimited"
                        : FREE_LIMITS.templates}
                  </Text>
                </Flex>
                <Progress
                  color="#003554"
                  value={
                    isPremium
                      ? 0
                      : (amplifyUser.currentTemplatesUsed /
                          (isEssential
                            ? ESSENTIAL_LIMITS.templates
                            : FREE_LIMITS.templates)) *
                        100
                  }
                />
              </>
            )}
          </Box>
          <Box ta={"center"} my={"sm"}>
            {amplifyUser !== null && (
              <>
                <Flex align={"center"} justify={"space-between"}>
                  <Text fz={"sm"}>
                    Transcription
                    {addCharacterS(amplifyUser?.currentTranscriptionsCreated)}
                  </Text>
                  <Text mx={"sm"}>
                    {amplifyUser === null
                      ? 0
                      : amplifyUser?.currentTranscriptionsCreated || 0}
                    {` / `}
                    {isEssential
                      ? ESSENTIAL_LIMITS.transcriptions
                      : isPremium
                        ? "Unlimited"
                        : FREE_LIMITS.transcriptions}
                  </Text>
                </Flex>
                <Progress
                  color="#003554"
                  value={
                    isPremium
                      ? 0
                      : (amplifyUser.currentTranscriptionsCreated /
                          (isEssential
                            ? ESSENTIAL_LIMITS.transcriptions
                            : FREE_LIMITS.transcriptions)) *
                        100
                  }
                />
              </>
            )}
          </Box>
          <Box ta={"center"}>
            {amplifyUser !== null && (
              <>
                <Flex align={"center"} justify={"space-between"}>
                  <Text fz={"sm"}>
                    Case{addCharacterS(amplifyUser?.currentCasesCreated)}{" "}
                    Created
                  </Text>
                  <Text mx={"sm"}>
                    {amplifyUser === null
                      ? 0
                      : amplifyUser?.currentCasesCreated || 0}
                    {` / `}
                    {isEssential
                      ? ESSENTIAL_LIMITS.cases
                      : isPremium
                        ? "Unlimited"
                        : FREE_LIMITS.cases}
                  </Text>
                </Flex>
                <Progress
                  color="#003554"
                  value={
                    isPremium
                      ? 0
                      : (amplifyUser.currentCasesCreated /
                          (isEssential
                            ? ESSENTIAL_LIMITS.cases
                            : FREE_LIMITS.cases)) *
                        100
                  }
                />
              </>
            )}
          </Box>
          <Flex align={"flex-end"} flex={1} justify={"flex-end"} mt={"md"}>
            <Button w={"150"} onClick={() => navigate("/plans")}>
              Change plan
            </Button>
          </Flex>
          {renderExtraTexts() && (
            <>
              <Divider my={"md"} />
              <Title size={"h5"}>Subscription Info</Title>
              {renderExtraTexts()}
            </>
          )}
        </Card>
      )}
    </Box>
  );
};
export default Billing;
